import { Navigate, Outlet, ScrollRestoration, createBrowserRouter } from 'react-router-dom';
import { PagePath } from 'constants/pagePath';
import { AuthGuardLayout, GuestLayout, LoginLayout } from 'views/layout';
import { Home } from 'views/pages/home';
import { LeaseFundProgramList, DomesticRealEstateItExplanationProgramList, ProgramList } from 'views/pages/programList';
import { DomesticRealEstateItExplanationSymbol } from 'views/pages/symbol/domesticRealEstate/itExplanation';
import { DomesticRealEstateSymbol } from 'views/pages/symbol/domesticRealEstate/apply';
import { LeaseFundSymbol } from 'views/pages/symbol/leaseFund';
import { Login } from 'views/pages/logins/login';
import { AgencyLogin } from 'views/pages/agencyLogin';
import { SendSMS } from 'views/pages/logins/sendSMS';
import { ReissuePassword } from 'views/pages/logins/reissuePassword';
import { ActivationCode } from 'views/pages/logins/activationCode';
import { LeaseFundContractProcedure } from 'views/pages/contractProcedure/leaseFund';
import { RequestHistory } from 'views/pages/requestHistory';
import { Position } from 'views/pages/position';
import { Notice } from 'views/pages/notice';
import { Contact } from 'views/pages/contact';
import { NoticeDetail } from 'views/pages/noticeDetail';
import { SubUser } from 'views/pages/subUser';
import { SubUserAdd } from 'views/pages/subUserAdd';
import { CompatibilityCheck, CompatibilityCheckHome } from 'views/pages/compatibilityCheck';
import { ConfirmCompatibilityCheck, ConfirmCompatibilityCheckHome } from 'views/pages/confirmCompatibilityCheck';
import { IssuanceDocument } from 'views/pages/issuanceDocument';
import { CorporateAccountOpen } from 'views/pages/accountOpen/corporate';
import { CorporateAccountOpenProfileDetails } from 'views/pages/accountOpenProfileDetails/corporateProfileDetails';
import { Faq } from 'views/pages/FAQ';
import { PersonalAccountOpen } from 'views/pages/accountOpen/personal';
import { PersonalAccountOpenProfileDetails } from 'views/pages/accountOpenProfileDetails/personalProfileDetails';
import {
  Client,
  CombinedError,
  Exchange,
  Operation,
  Provider,
  cacheExchange,
  createClient,
  fetchExchange,
  mapExchange,
} from 'urql';
import { AuthConfig, AuthUtilities, authExchange } from '@urql/exchange-auth';

import { AccountInfo } from 'views/pages/accountInfo';
import { GraphQLError } from 'graphql';
import { markCurrentToastProtected, useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { useAuthGuard, useAuthHandler } from 'views/auth/useAuth';
import { ChangePassword } from 'views/pages/changePassword';
import { GlobalLoading } from 'views/mutual/globalLoading';
import { GlobalToast } from 'views/mutual/globalToast';
import { ErrorPage } from 'views/pages/errorPage';
import { OverseasRealEstateSymbol } from 'views/pages/symbol/overseasRealEstate';
import { OverseasRealEstateContractProcedure } from 'views/pages/contractProcedure/overseasRealEstate';
import { OverseasRealEstateFatcaWrapper } from 'views/pages/symbol/overseasRealEstate/fatca';

const isAuthError = (error: CombinedError) => {
  if (error.graphQLErrors.some((e: GraphQLError) => e.originalError?.message === '認証エラー')) {
    return true;
  } else if (error.response?.status === 401) {
    // あえて設定する必要があるがtokenを空にすると401が返ってくる
    return true;
  } else {
    return false;
  }
};

// 代行ログイン中の操作拒否を表す AppSync の errorType（util.error の第2引数）。
// サーバ側（C#の ResultCode 名・AppSync GetAccount の util.error）と同一の識別子。
const AGENCY_FORBIDDEN_ERROR_TYPE = 'AgencyLogin_Forbidden';

// 代行ログイン中の操作拒否エラーを返す。
// AppSyncエラーは通常一律の汎用メッセージで表示しているが、このエラーだけは
// AppSyncが返したメッセージをそのまま表示したい。表示文言ではなく errorType で限定的に特定するため、
// 文言変更に対して頑健（メッセージ文字列に依存しない）。
const findAgencyForbiddenError = (error: CombinedError) =>
  error.graphQLErrors.find((e: GraphQLError) => {
    // urql のエラー復元方法により errorType の格納先が変わりうるため、3箇所を確認する
    const self = e as unknown as { errorType?: string };
    const raw = e.originalError as { errorType?: string } | undefined;
    const ext = e.extensions as { errorType?: string } | undefined;
    return (
      self?.errorType === AGENCY_FORBIDDEN_ERROR_TYPE ||
      raw?.errorType === AGENCY_FORBIDDEN_ERROR_TYPE ||
      ext?.errorType === AGENCY_FORBIDDEN_ERROR_TYPE
    );
  });

const createUrqlClient = (
  getToken: () => string,
  raiseLogoutEvent: () => void,
  showErrorMessage: (message: string, durationMs?: number) => void,
) =>
  createClient({
    url: process.env.REACT_APP_API_URL!,
    // 指定しない場合はcache-firstがデフォルトなので各QueryでrequestPolicyを指定しないとcacheのみになるのでnetwork-onlyにする
    // デフォルトは変更するが各QueryではrequestPolicyは指定する
    requestPolicy: 'network-only',
    exchanges: [
      cacheExchange,
      mapExchange({
        onError(error) {
          // 認証エラーの場合は何もしない 先にauthExchangeが呼ばれるが念のため
          if (isAuthError(error)) {
            return;
          }

          // 代行ログイン中の操作拒否エラーのみ、AppSyncが返したメッセージをそのまま表示する
          // （他のAppSyncエラーは下の汎用メッセージのまま）
          const agencyForbiddenError = findAgencyForbiddenError(error);
          if (agencyForbiddenError) {
            showErrorMessage(agencyForbiddenError.message, 4000);
            // 各画面の showError(undefined) で上書きされないよう、このトーストを保護する
            markCurrentToastProtected();
            return;
          }

          // API全体で共通で他の操作した場合にも消えないため時間指定で消す
          if (!error.response) {
            showErrorMessage('通信エラーが発生しました。通信環境を確認して再度お試しください。', 4000);
          } else if (error.response.status === 503) {
            // 503はメンテナンスでログイン画面に戻す(ログイン画面に戻すとWAFによってメンテナンスページを表示させる処理が動く)
            raiseLogoutEvent();
          } else if (error.response.status !== 200) {
            // APIサーバに到達できない場合などで200以外の場合を想定
            showErrorMessage('エラーが発生しました。しばらくしてから再度お試しください。', 4000);
          } else if (error.graphQLErrors) {
            // 型エラーなどバグレベルの場合
            showErrorMessage('エラーが発生しました。もう一度お試し頂くか、お問い合わせください。', 4000);
          }
        },
      }),
      authExchange(async (utils: AuthUtilities) => {
        return {
          addAuthToOperation(operation: Operation) {
            return operation;
          },
          didAuthError(error: CombinedError, _) {
            return isAuthError(error);
          },
          async refreshAuth() {
            raiseLogoutEvent();
          },
        } as AuthConfig;
      }) as Exchange,
      fetchExchange,
    ],
    fetchOptions: () => {
      return {
        headers: {
          authorization: getToken() || '-',
        },
      };
    },
  });

let client: Client;
const Root = () => {
  const { getToken, redirectToLogin } = useAuthHandler();
  const { showError } = useGlobalToastHandler();
  client = client ?? createUrqlClient(getToken, redirectToLogin, showError);

  return (
    <Provider value={client}>
      <ScrollRestoration />
      <Outlet />
      <GlobalLoading />
      <GlobalToast />
    </Provider>
  );
};

// ログイン済みならHOME 未ログインならログイン画面に遷移
const SwitchTop = () => {
  const { isLogin } = useAuthGuard();
  if (isLogin) {
    return <Navigate to={PagePath.Home} />;
  } else {
    // useAuthGuard内でログインに遷移させているのでここでは何もしない
    return <></>;
  }
};

export const router = createBrowserRouter([
  {
    path: PagePath.Root,
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: PagePath.Root,
        element: <SwitchTop />,
      },
      // 代行ログイン受け口（認証ガード対象外。管理画面からpostMessageでトークンを受け取る）
      {
        path: PagePath.AgencyLogin,
        element: <AgencyLogin />,
      },
      // ログイン関連
      {
        path: PagePath.Root,
        element: <LoginLayout />,
        children: [
          // ログイン
          {
            path: PagePath.Login,
            element: <Login />,
          },
          // SMS認証
          {
            path: PagePath.SendSMS,
            element: <SendSMS />,
          },
          // PW再発行
          {
            path: PagePath.ReissuePassword,
            element: <ReissuePassword />,
          },
          // アクティベーションコード入力画面
          {
            path: PagePath.ActivationCode,
            element: <ActivationCode />,
          },
        ],
      },
      // ログイン関連以外の未ログインのページ
      {
        path: PagePath.Root,
        element: <GuestLayout />,
        children: [
          // 法人口座開設事前質問
          {
            path: PagePath.AccountOpen,
            element: <CorporateAccountOpen />,
          },
          // 法人口座開設本登録回答
          {
            path: `${PagePath.AccountOpenProfileDetails}:uniqueId/`,
            element: <CorporateAccountOpenProfileDetails />,
          },
          // 個人口座開設事前質問
          {
            path: PagePath.PersonalAccountOpen,
            element: <PersonalAccountOpen />,
          },
          // 個人口座開設本登録回答
          {
            path: `${PagePath.PersonalAccountOpenProfileDetails}:uniqueId/`,
            element: <PersonalAccountOpenProfileDetails />,
          },
          // FAQ
          {
            path: `${PagePath.FaqLink}:id/`,
            element: <Faq />,
          },
        ],
      },
      // ログイン済みのページ
      {
        path: PagePath.Root,
        element: <AuthGuardLayout />,
        children: [
          // ホーム
          {
            path: PagePath.Home,
            element: <Home />,
          },
          // 案件一覧
          {
            path: PagePath.ProgramList,
            element: <ProgramList />,
          },
          // 案件詳細（リースファンド）
          {
            path: `${PagePath.LeaseFundSymbolDetail}:symbol/`,
            element: <LeaseFundSymbol />,
          },
          // 案件詳細（国内不動産_IT重説）
          {
            path: `${PagePath.DomesticRealEstateItExplanationSymbolDetail}:symbol/`,
            element: <DomesticRealEstateItExplanationSymbol />,
          },
          // 案件詳細（国内不動産）
          {
            path: `${PagePath.DomesticRealEstateSymbolDetail}:symbol/:id/`,
            element: <DomesticRealEstateSymbol />,
          },
          // 案件詳細（海外不動産）
          {
            path: `${PagePath.OverseasRealEstateSymbolDetail}:symbol/`,
            element: <OverseasRealEstateSymbol />,
          },
          // 出資案件
          {
            path: PagePath.Position,
            element: <Position />,
          },
          // 申込履歴
          {
            path: PagePath.RequestHistory,
            element: <RequestHistory />,
          },
          // 交付書面
          {
            path: `${PagePath.IssuanceDocument}`,
            element: <IssuanceDocument />,
          },
          // ご契約手続き
          //リースファンド
          {
            path: `${PagePath.LeaseFundContractProcedure}:id/`,
            element: <LeaseFundContractProcedure />,
          },
          //海外不動産
          {
            path: `${PagePath.OverseasRealEstateContractProcedure}:id/`,
            element: <OverseasRealEstateContractProcedure />,
          },
          // 利用者登録
          {
            path: PagePath.SubUser,
            element: <SubUser />,
          },
          // 利用者追加
          {
            path: PagePath.SubUserAdd,
            element: <SubUserAdd />,
          },
          // 適合性チェック回答入力
          {
            path: PagePath.CompatibilityCheck,
            element: <CompatibilityCheckHome />,
          },
          // 適合性チェック結果確認
          {
            path: PagePath.ConfirmCompatibilityCheck,
            element: <ConfirmCompatibilityCheckHome />,
          },
          // お知らせ
          {
            path: PagePath.Notice,
            element: <Notice />,
          },
          // お問い合わせ
          {
            path: PagePath.Contact,
            element: <Contact />,
          },
          // お知らせ詳細
          {
            path: `${PagePath.NoticeDetail}:id/`,
            element: <NoticeDetail />,
          },
          // 登録内容の確認・変更
          {
            path: PagePath.AccountInfo,
            element: <AccountInfo />,
          },
          // パスワード変更
          {
            path: PagePath.ChangePassword,
            element: <ChangePassword />,
          },
          // FAQ
          {
            path: `${PagePath.Faq}:id/`,
            element: <Faq />,
          },
        ],
      },
    ],
  },
]);
